import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  LinearProgress,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Download,
  FilterList,
  EventAvailable,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const AttendanceAnalytics = () => {
  const navigate = useNavigate();
  const { isInInstituteContext, selectedInstitute } = useSelector(
    (state) => state.instituteContext
  );
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";
  const userInstitute = user?.institute?.name || "ABC Engineering College";
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const [instituteFilter, setInstituteFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [instituteAttendance, setInstituteAttendance] = useState([]);
  const [overall, setOverall] = useState({ present: 0, late: 0, absent: 0, attended: 0, rate: 0, studentSessionDenom: 0 });
  const [period, setPeriod] = useState({ startDate: null, endDate: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const instituteService = (await import("../../services/instituteService")).default;
        const deptService = (await import("../../services/departmentService")).default;

        if (canViewGlobalData) {
          const instRes = await instituteService.getInstitutes({ page: 1, limit: 50 });
          if (mounted && instRes?.data?.data) setInstitutes(instRes.data.data);
        }
        const deptRes = await deptService.getAllDepartments({ page: 1, limit: 100 });
        if (mounted && deptRes?.data?.data) setDepartments(deptRes.data.data);

        const res = await analyticsService.getAttendance({
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
          ...(departmentFilter ? { departmentId: departmentFilter } : {}),
          ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
          filter:
            periodFilter === "week"
              ? "weekly"
              : periodFilter === "month"
              ? "monthly"
              : periodFilter === "year"
              ? "yearly"
              : "monthly",
        });
        if (mounted) {
          const data = res?.data?.data ?? res?.data ?? {};
          setInstituteAttendance(data.instituteAttendance || []);
          setOverall(
            data.overall || { present: 0, late: 0, absent: 0, attended: 0, rate: 0, studentSessionDenom: 0 }
          );
          setPeriod(data.period || { startDate: null, endDate: null });
        }
      } catch (e) {
        console.warn("Failed to load attendance analytics:", e?.message || e);
        if (mounted) {
          setInstituteAttendance([]);
          setOverall({ present: 0, late: 0, absent: 0, attended: 0, rate: 0, studentSessionDenom: 0 });
          setPeriod({ startDate: null, endDate: null });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [canViewGlobalData, instituteFilter, departmentFilter, periodFilter, fromDate, toDate]);

  const overallData = {
    labels: ["Present", "Late", "Absent"],
    datasets: [
      {
        data: [overall.present, overall.late, overall.absent],
        backgroundColor: ["#4caf50", "#ff9800", "#f44336"],
        borderWidth: 0,
      },
    ],
  };

  const weeklyTrendData = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    datasets: [
      {
        label: "Attendance Rate %",
        data: [92, 94, 91, 93, 90, 88],
        backgroundColor: "#1976d2",
      },
    ],
  };

  const columns = [
    { field: "institute", headerName: "Institute", flex: 1, minWidth: 200 },
    { field: "students", headerName: "Students", width: 110 },
    { field: "sessionsTotal", headerName: "Sessions", width: 110 },
    { field: "present", headerName: "Present", width: 100 },
    { field: "late", headerName: "Late", width: 90 },
    { field: "absent", headerName: "Absent", width: 100 },
    {
      field: "rate",
      headerName: "Attendance %",
      width: 180,
      renderCell: (params) => (
        <Box
          sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}
        >
          <LinearProgress
            variant="determinate"
            value={params.value}
            sx={{
              flexGrow: 1,
              height: 8,
              borderRadius: 4,
              bgcolor: "grey.200",
              "& .MuiLinearProgress-bar": {
                bgcolor:
                  params.value >= 90
                    ? "success.main"
                    : params.value >= 80
                    ? "warning.main"
                    : "error.main",
              },
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {params.value}%
          </Typography>
        </Box>
      ),
    },
  ];

  // Get page title based on context
  const getPageTitle = () => {
    if (canViewGlobalData) return "Attendance Analytics";
    if (isInInstituteContext) return `Attendance - ${selectedInstitute?.name}`;
    return `Attendance - ${userInstitute}`;
  };

  const getPageSubtitle = () => {
    if (canViewGlobalData) return "View attendance rates across all institutes";
    if (isInInstituteContext)
      return `Attendance overview for ${selectedInstitute?.name}`;
    return `Attendance overview for your institute`;
  };

  const filteredInstituteAttendance = instituteAttendance;

  const stats = [
    { label: "Attendance %", value: `${overall.rate || 0}%`, color: "success.main" },
    {
      label: "Present",
      value: (overall.present || 0).toLocaleString(),
      color: "primary.main",
    },
    {
      label: "Absent",
      value: (overall.absent || 0).toLocaleString(),
      color: "error.main",
    },
    {
      label: "Late",
      value: (overall.late || 0).toLocaleString(),
      color: "warning.main",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageSubtitle()}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export Report
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={periodFilter}
                  label="Period"
                  onChange={(e) => setPeriodFilter(e.target.value)}
                >
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="From"
                format={DATE_DISPLAY_FORMAT}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(val) => setFromDate(toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="To"
                format={DATE_DISPLAY_FORMAT}
                value={toDate ? new Date(toDate) : null}
                onChange={(val) => setToDate(toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                disabled={!fromDate && !toDate}
                sx={{ height: 40 }}
              >
                Clear Date Range
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Institute-wise Attendance
          </Typography>
          {period?.startDate && period?.endDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Period: {period.startDate} to {period.endDate}
            </Typography>
          )}
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={filteredInstituteAttendance}
              columns={columns}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              disableRowSelectionOnClick
              getRowId={(row) => row.id ?? row.institute}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AttendanceAnalytics;
